import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import { useMutation } from '@apollo/client';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ParseFileResponse, ParseFileVariables, OnboardingMutations } from '../../../../../graphql/onboarding.graphql';
import { CsvUploadType, fileNameToFileType } from '../../../../../types/csvUploadConfiguration';
import { t } from '../../../../../types/translation/Translator';
import { Button, ButtonVariant } from '../../../../components/Button/Button';
import { FileInput } from '../../../../components/File';
import FileItem from '../../../../components/File/FileItem';
import Icon, { IconType } from '../../../../icons/Icon';
import { LoadingSpinner } from '../../../Common/LoadingSpinner';
import { OnboardingStep, OnboardingTitle, OnboardingSubtitle } from '../../components';
import OnboardingPaneHeader from '../../components/OnboardingPaneHeader';
import { OnboardingData, OnboardingRoutes } from '../..';
import { flattenOnboardingEntities, mapParsedFileData } from '../../model/OnboardingData';
import { ToastType } from '../../../../components/Toast';
import { useToast } from '../../../../../providers/ToastProvider';
import { FileUtils } from '../../../../../types/file';

interface OnboardingStockImportPaneProps {
  data: OnboardingData;
  setData: (data: OnboardingData) => void;
}

export default function OnboardingStockImportPane({ data, setData }: OnboardingStockImportPaneProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const stockFiles = useMemo(() => {
    return [...data.files.entries()].filter(([id, file]) => file.type === CsvUploadType.product);
  }, [data]);

  const [upload, { loading }] = useMutation<ParseFileResponse, ParseFileVariables>(OnboardingMutations.parseFile);

  const handleFile = async (files: File[] | FileList | null) => {
    if (!files) return;
    const file = files instanceof File ? files : files[0];

    await upload({
      variables: {
        input: {
          companyId: data.company.id,
          stockLocationId: '',
          type: CsvUploadType.product,
          deleteExisting: false,
          csvFileAsBase64Data: await FileUtils.fileToBase64(file),
          fileType: fileNameToFileType(file.name),
        },
        state: flattenOnboardingEntities(data.summary),
      },
      onError: error => showToast({ title: error.message, type: ToastType.error }),
      onCompleted: response => {
        setData(
          data.import({
            file: { name: file.name, size: file.size },
            data: mapParsedFileData(response.parseFile),
            type: CsvUploadType.product,
          }),
        );
      },
    });
  };
  return (
    <div className='grid gap-4 overflow-hidden'>
      <OnboardingPaneHeader back={() => navigate(OnboardingRoutes.stockLocationOverview)} />

      <div className='grid gap-1'>
        <OnboardingStep />
        <OnboardingTitle text={'Add your Stock'} />
        <OnboardingSubtitle
          text={
            'Define where your inventory is stored by adding stock locations. This helps you track and manage items efficiently across different sites.'
          }
        />
      </div>

      <p className='text-sm font-bold text-black'>
        Before you upload your file, make sure your file is{' '}
        <a
          className='inline-flex items-center text-blue-600'
          href={`${process.env.REACT_APP_STORAGE_URL}/ventory/product_template.xlsx`}
        >
          ready to be imported
          <span className='ml-1 inline-block'>
            <Icon type={IconType.share4} stroke={'stroke-black'} size={'h-4 w-4'} />
          </span>
        </a>
      </p>

      <div className='grid gap-2'>
        <p className='text-sm font-medium text-gray-900'>Upload your file to add your stock to your company</p>
        <FileInput onFiles={handleFile} acceptedTypes={'.csv,.xlsx'} />
      </div>

      {stockFiles.length ? (
        <div className='flex items-center gap-2 stroke-blue-600'>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <Icon type={IconType.checkCircle} stroke={'stroke-success-500'} size={'h-4 w-4'} />
          )}
          <p className='text-sm text-gray-600'>
            {data.summary.stockLocations.size} Stock Locations with {data.summary.bins.size} Bins holding{' '}
            {data.summary.products.size} Products.
          </p>
        </div>
      ) : null}

      {stockFiles.length ? (
        <div className='flex flex-grow flex-col gap-2 overflow-auto'>
          {stockFiles.map(([id, file], index) => (
            <FileItem key={id} file={file.file} onRemove={() => data.files.delete(id) && setData(data.update())} />
          ))}
        </div>
      ) : null}

      <div className='flex gap-2'>
        <Button
          text={t(TranslationKey.next).singular.label}
          onClick={() => navigate(OnboardingRoutes.users)}
          disabled={!stockFiles.length}
        />
        {!stockFiles.length ? (
          <Button
            text={'Skip this step'}
            onClick={() => navigate(OnboardingRoutes.users)}
            variant={ButtonVariant.linkPrimary}
          />
        ) : null}
      </div>
    </div>
  );
}
